import { useEffect, useMemo, useState } from "react";
import { Wrench, Plus, Search, Pencil, Trash2, Power, X, Loader2, CheckCircle2 } from "lucide-react";
import { adminFetch } from "@/lib/api";
import { Loader, Card, EmptyState } from "@/components/common";
import { cn } from "@/lib/utils";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export const SERVICE_CATEGORIES = [
  ["officina", "Officina"],
  ["gommista", "Gommista"],
  ["carrozzeria", "Carrozzeria"],
  ["elettrauto", "Elettrauto"],
  ["soccorso_stradale", "Soccorso stradale"],
  ["frigorista", "Frigorista"],
  ["lavaggio_camion", "Lavaggio camion"],
  ["ricambi", "Ricambi"],
  ["parcheggio", "Parcheggio"],
  ["altri_servizi", "Altri servizi"],
];
const CAT_LABEL = Object.fromEntries(SERVICE_CATEGORIES);

const DEFAULTS = {
  name: "", company_name: "", vat_number: "", category: "officina", phone: "", email: "",
  address: "", city: "", province: "", area: "", hours: "", website: "", notes: "",
  rating: null, active: true, logo_id: "", services_offered: "", emergency_h24: false,
  plan: "base", verified: false, visibility_start: "", visibility_end: "", priority: 0, admin_notes: "",
};

export default function AdminServices() {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterCity, setFilterCity] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(DEFAULTS);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = () => {
    setLoading(true);
    adminFetch("/admin/providers").then(setProviders).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(DEFAULTS);
    setError("");
    setDialogOpen(true);
  };
  const openEdit = (p) => {
    setEditingId(p.id);
    setForm({ ...DEFAULTS, ...p });
    setError("");
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name.trim() || form.name.trim().length < 2) {
      setError("Inserisci il nome dell'attività/fornitore.");
      return;
    }
    if (!form.phone.trim() || form.phone.trim().length < 6) {
      setError("Inserisci un numero di telefono valido.");
      return;
    }
    setSaving(true);
    setError("");
    const payload = { ...form, name: form.name.trim(), company_name: (form.company_name || form.name).trim() };
    try {
      if (editingId) {
        const updated = await adminFetch(`/admin/providers/${editingId}`, { method: "PATCH", body: JSON.stringify(payload) });
        setProviders((old) => old.map((it) => (it.id === editingId ? updated : it)));
      } else {
        const created = await adminFetch("/admin/providers", { method: "POST", body: JSON.stringify(payload) });
        setProviders((old) => [created, ...old]);
      }
      setDialogOpen(false);
    } catch (e) {
      setError(e.message || "Salvataggio non riuscito. Riprova.");
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (p) => {
    const updated = await adminFetch(`/admin/providers/${p.id}/toggle`, { method: "POST", body: "{}" }).catch(() => null);
    if (updated) setProviders((old) => old.map((it) => (it.id === p.id ? updated : it)));
  };
  const remove = async (p) => {
    if (!window.confirm(`Eliminare "${p.name}"? L'operazione è irreversibile.`)) return;
    await adminFetch(`/admin/providers/${p.id}`, { method: "DELETE" }).catch(() => {});
    setProviders((old) => old.filter((it) => it.id !== p.id));
  };

  const cities = useMemo(
    () => Array.from(new Set(providers.map((p) => (p.city || "").trim()).filter(Boolean))).sort(),
    [providers]
  );
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return providers.filter((p) => {
      if (filterCategory && p.category !== filterCategory) return false;
      if (filterCity && (p.city || "").toLowerCase() !== filterCity.toLowerCase()) return false;
      if (term && !`${p.name} ${p.city} ${p.category} ${p.services_offered} ${p.phone}`.toLowerCase().includes(term)) return false;
      return true;
    });
  }, [providers, q, filterCategory, filterCity]);

  const inputCls = "min-h-[46px] w-full rounded-xl border border-border bg-card px-3 text-sm outline-none focus:border-primary";
  const fieldLabel = "mb-1 block text-xs font-bold text-card-foreground";

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-extrabold text-foreground">Fornitori ({filtered.length})</p>
        <button data-testid="admin-service-add" onClick={openCreate} className="flex items-center gap-1.5 rounded-full bg-primary px-3.5 py-2 text-xs font-extrabold text-primary-foreground">
          <Plus className="h-4 w-4" /> Aggiungi servizio
        </button>
      </div>

      <div className="flex min-h-[46px] items-center gap-2.5 rounded-xl border border-border bg-card px-3">
        <Search className="h-5 w-5 text-muted-foreground" />
        <input data-testid="admin-service-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca nome, città, telefono..." className="h-11 flex-1 bg-transparent text-sm outline-none" />
      </div>
      <div className="flex flex-wrap gap-2">
        <select data-testid="admin-service-filter-category" value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)} className="rounded-xl border border-border bg-card px-3 py-2 text-sm font-semibold outline-none">
          <option value="">Tutte le categorie</option>
          {SERVICE_CATEGORIES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <select data-testid="admin-service-filter-city" value={filterCity} onChange={(e) => setFilterCity(e.target.value)} className="rounded-xl border border-border bg-card px-3 py-2 text-sm font-semibold outline-none">
          <option value="">Tutte le città</option>
          {cities.map((ci) => <option key={ci} value={ci}>{ci}</option>)}
        </select>
      </div>

      {loading ? (
        <Loader />
      ) : filtered.length === 0 ? (
        <EmptyState icon={Wrench} title="Nessun fornitore" text="Aggiungi il primo servizio con il pulsante in alto o modifica i filtri." />
      ) : (
        filtered.map((p) => (
          <Card key={p.id} data-testid={`admin-provider-${p.id}`} className="space-y-2">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <p className="truncate font-extrabold text-foreground">{p.name}</p>
                <p className="truncate text-xs text-muted-foreground">
                  {CAT_LABEL[p.category] || p.category}
                  {p.city ? ` · ${p.city}${p.province ? ` (${p.province})` : ""}` : ""}
                </p>
                <p className="truncate text-xs text-muted-foreground">{p.phone}{p.email ? ` · ${p.email}` : ""}</p>
                {p.services_offered ? <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{p.services_offered}</p> : null}
              </div>
              <span className={cn("shrink-0 rounded-full px-2.5 py-1 text-[11px] font-extrabold", p.active ? "bg-[hsl(var(--success))] text-white" : "bg-secondary text-secondary-foreground")}>
                {p.active ? "Attivo" : "Non attivo"}
              </span>
            </div>
            <div className="flex flex-wrap gap-2 border-t border-border pt-2">
              <button data-testid={`admin-provider-edit-${p.id}`} onClick={() => openEdit(p)} className="flex items-center gap-1.5 rounded-lg border border-primary px-3 py-1.5 text-xs font-extrabold text-primary"><Pencil className="h-4 w-4" /> Modifica</button>
              <button data-testid={`admin-provider-toggle-${p.id}`} onClick={() => toggle(p)} className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-extrabold text-foreground"><Power className="h-4 w-4" /> {p.active ? "Disattiva" : "Attiva"}</button>
              <button data-testid={`admin-provider-delete-${p.id}`} onClick={() => remove(p)} className="flex items-center gap-1.5 rounded-lg border border-destructive px-3 py-1.5 text-xs font-extrabold text-destructive"><Trash2 className="h-4 w-4" /> Elimina</button>
            </div>
          </Card>
        ))
      )}

      <Dialog open={dialogOpen} onOpenChange={(o) => !saving && setDialogOpen(o)}>
        <DialogContent className="max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? "Modifica servizio" : "Nuovo servizio"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2.5">
            <div>
              <label className={fieldLabel}>Nome attività / fornitore *</label>
              <input data-testid="admin-service-field-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className={fieldLabel}>Categoria *</label>
              <select data-testid="admin-service-field-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls}>
                {SERVICE_CATEGORIES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className={fieldLabel}>Descrizione</label>
              <textarea data-testid="admin-service-field-description" value={form.services_offered} onChange={(e) => setForm({ ...form, services_offered: e.target.value })} className={cn(inputCls, "min-h-[70px] py-2")} placeholder="Servizi offerti, specializzazioni..." />
            </div>
            <div className="flex gap-2.5">
              <div className="flex-1">
                <label className={fieldLabel}>Telefono *</label>
                <input data-testid="admin-service-field-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} />
              </div>
              <div className="flex-1">
                <label className={fieldLabel}>Email</label>
                <input data-testid="admin-service-field-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
              </div>
            </div>
            <div>
              <label className={fieldLabel}>Indirizzo</label>
              <input data-testid="admin-service-field-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className={inputCls} />
            </div>
            <div className="flex gap-2.5">
              <div className="flex-1">
                <label className={fieldLabel}>Città</label>
                <input data-testid="admin-service-field-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className={inputCls} />
              </div>
              <div className="w-28">
                <label className={fieldLabel}>Provincia</label>
                <input data-testid="admin-service-field-province" value={form.province} onChange={(e) => setForm({ ...form, province: e.target.value.toUpperCase().slice(0, 4) })} className={inputCls} />
              </div>
            </div>
            <div>
              <label className={fieldLabel}>Sito web</label>
              <input data-testid="admin-service-field-website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className={inputCls} placeholder="https://" />
            </div>
            <button data-testid="admin-service-field-active" onClick={() => setForm({ ...form, active: !form.active })} className="flex items-center gap-2.5">
              <span className={cn("flex h-6 w-6 items-center justify-center rounded-md border-2", form.active ? "border-primary bg-primary" : "border-muted-foreground bg-card")}>
                {form.active ? <CheckCircle2 className="h-4 w-4 text-primary-foreground" /> : null}
              </span>
              <span className="text-sm font-bold text-card-foreground">Servizio attivo (visibile agli utenti)</span>
            </button>
            {error ? <p data-testid="admin-service-error" className="text-sm font-bold text-destructive">{error}</p> : null}
            <button data-testid="admin-service-save" onClick={save} disabled={saving} className="mt-1 flex min-h-[50px] w-full items-center justify-center gap-2 rounded-xl bg-primary text-sm font-extrabold text-primary-foreground disabled:opacity-60">
              {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : null} {editingId ? "SALVA MODIFICHE" : "AGGIUNGI SERVIZIO"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
